import {
  Stack,
  type StackProps,
  styled,
  type Theme,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";

import { AskMaraStarSvg } from "../assets/svg";
import { mergeSx } from "../utils/mergeSx";

// biome-ignore lint/style/useNamingConvention: is a type used for variant styling
export type TagVariant = "light" | "gradient" | "outlined";

export interface TagProps extends StackProps {
  /** Tag label. Styled according to Figma label tokens. */
  label: string;
  /** Tag start icon. Icon shown before label. Defaults to <AskMaraStar/>. */
  startIcon?: ReactNode;
  /** Tag end icon. Icon shown after label. Defaults to none. */
  endIcon?: ReactNode;
  /** Style variant ('light' | 'gradient' | 'outlined', per Figma spec). Defaults to 'light'. */
  variant?: TagVariant;
}

// biome-ignore lint/style/useNamingConvention: is a type used
type TagVariantStyle = {
  background?: string;
  backgroundColor?: string;
  border?: string;
  borderColor?: string;
};

// Resolves styles from the `styled` callback's theme so runtime theme changes are respected.
const getVariantStyle = (
  variant: TagVariant,
  theme: Theme,
): TagVariantStyle => {
  const variantMap: Record<TagVariant, TagVariantStyle> = {
    light: {
      backgroundColor: theme.palette.support[50],
    },
    gradient: {
      background: `linear-gradient(78deg, #E7FCF5 5.99%, #EDEFFF 104.08%)`,
    },
    outlined: {
      border: `1px solid transparent`,
      background: `linear-gradient(${theme.palette.common.white}, ${theme.palette.common.white}) padding-box, linear-gradient(78deg, #E7FCF5 5.99%, #EDEFFF 104.08%) border-box`,
    },
  };
  return variantMap[variant];
};

const IconWrapper = styled(Stack)({
  width: 16,
  height: 16,
  "& svg": {
    width: 16,
    height: 16,
  },
});

const TagRoot = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "variant",
})<{ variant: TagVariant }>(({ variant, theme }) => ({
  ...getVariantStyle(variant, theme),
  width: "fit-content",
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(0.5),
}));

export function Tag({
  label,
  startIcon = <AskMaraStarSvg />,
  endIcon,
  variant = "light",
  sx,
  ...props
}: TagProps) {
  return (
    <TagRoot
      direction="row"
      sx={mergeSx({ display: label ? "inline-flex" : "none" }, sx)}
      variant={variant}
      {...props}
    >
      {startIcon && <IconWrapper>{startIcon}</IconWrapper>}
      <Typography color="secondary.dark" variant="overline">
        {label}
      </Typography>
      {endIcon && <IconWrapper>{endIcon}</IconWrapper>}
    </TagRoot>
  );
}
